/*
 * Ryan O'Dowd
 * 2024-03-01
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useEffect,
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import {
  apiUpdateGoal,
  fetchBookNames,
  fetchBooks,
  fetchChapters,
  // @TODO: fetchClassroomGoal,
  fetchClassroomGoals,
  fetchClassroomStudents,
  fetchGoalProgress,
  fetchMinigames,
  fetchTags,
  fetchTranslations,
} from '@utils/store/actions';
import Accordion from '@components/common/Accordion';
import Banner from '@components/common/Banner';
import Button from '@components/common/Button';
import DraggableFlatList from 'react-native-draggable-flatlist';
import EditGoalDialog from './EditGoalDialog';
import EditModuleDialog from './EditModuleDialog';
import IconButton from '@components/common/IconButton';
import LinearProgress from '@components/common/LinearProgress';
import Module from './Module';
import PublishButton from './PublishButton';
import StudentAssignmentsDialog from './StudentAssignmentsDialog';
// @TODO: import StudentProgressTable from './StudentProgressTable';
import Table from '@components/common/Table';
import Text from '@components/common/Text';
import Tooltip from '@components/common/Tooltip';
import WebPageStub from '@website/containers/WebPageStub';
import WebsiteSection from '@website/components/WebsiteSection';
import {
  format as dateFnsFormat,
  // @TODO: formatRelative as dateFnsFormatRelative,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const DRAFT_TEXT = `This is a draft and won't be available to students until it is published.  If you are ready to publish it, click the "Publish" button.`; // @TODO: combine with EditGoalDialog
const PUBLISHED_TEXT = (datePublished) => `Published on ${dateFnsFormat(new Date(datePublished), 'MMMM d, yyyy')} and currently available to students.`; // @TODO: combine with EditGoalDialog

const ClassroomGoal = () => {
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);
  const dispatch = hooks.useTypedDispatch();
  const locationState = hooks.useLocationState();

  const [_isEditGoalDialogOpen, setIsEditGoalDialogOpen] = useState(false);
  const [_isNewModuleDialogOpen, setIsNewModuleDialogOpen] = useState(false);
  const [_selectedStudentId, setSelectedStudentId] = useState(null);

  const classroomStudents = hooks.useTypedSelector((state) => state.classroomStudents[locationState.classroomId] || []);

  const goal = hooks.useTypedSelector((state) => state.classroomGoals[locationState.goalId]) || {}; // @TODO: shouldn't need the `|| {}`

  const isOakwoodPublicGoal = goal?.classroom_goal_metadata?.is_public === 1;

  const goalProgress = hooks.useTypedSelector((state) => state.goalProgress[goal.id]) || {}; // @TODO: need {}?

  const isAdmin = hooks.useIsAdmin();

  useEffect(() => { // @TODO: this should go in App and be called coniditionally if user is logged in
    // @TODO: dispatch(fetchSchool(locationState.schoolId));
    dispatch(fetchBooks());
    dispatch(fetchChapters());
    dispatch(fetchTranslations());
    dispatch(fetchBookNames());
    dispatch(fetchClassroomGoals(locationState.classroomId));
    // @TODO: dispatch(fetchClassroomGoal(locationState.classroomId, goal.id));
    dispatch(fetchClassroomStudents(locationState.classroomId));
    dispatch(fetchMinigames());
    if (isAdmin) {
      dispatch(fetchTags());
    }
  }, [dispatch, locationState.classroomId, isAdmin, goal.id]);

  useEffect(() => {
    if (!isOakwoodPublicGoal) {
      // oakwood classroom public goals shouldn't fetch student progress
      dispatch(fetchGoalProgress(locationState.classroomId, goal.id));
    }
  }, [dispatch, locationState.classroomId, goal.id, isOakwoodPublicGoal]);

  const [_moduleOrderOverrides, setModuleOrderOverrides] = useState(goal.modules);

  useEffect(() => {
    setModuleOrderOverrides(goal.modules);
  }, [goal.modules]);

  if (!goal || !goal.classroom_goal_metadata || !goalProgress) {
    return (
      <WebPageStub
        hideTabs={true}// @TODO: instead of hiding tabs, should we show different tabs for teacher dashboard? i think we should consider it once this single page becomes too complex
      >

        <WebsiteSection
          style={styles.noVerticalPadding}
          isDarkBackground={false}
        >
          <Button// @TODO: shouldn't be full width
            color={styles.colors.primary}
            mode='text'
            icon='arrow-left'
            onPress={() => navigation.navigate(`/schools/${locationState.schoolId}/classrooms/${locationState.classroomId}`)}// @TODO: this is delayed...
          >
            Back to classroom{/* @TODO: i18n */}
          </Button>
        </WebsiteSection>

        <WebsiteSection isDarkBackground={false}>
          <Text>Goal not found.</Text>{/* @TODO: i18n */}
        </WebsiteSection>
      </WebPageStub>
    );
  }

  // @TODO: can memoize these: {
  const numStudents = Object.values(goalProgress).length;
  const numLessonsRequiredByEachStudent = numStudents ? Object.values(goalProgress).flatMap((student) => Object.values(student)).map((gp) => gp.nlr).reduce((a, b) => a + b, 0) / numStudents : 0;
  // @TODO: }

  // @TODO: this component is rendered too many times

  const studentTableRows = Object.entries(goalProgress).map(([studentId, assignments]) => { // @TODO: convert these to accordion? when expanded, show all info for just this student (and class average)
    const numCompleted = Object.values(assignments).reduce((acc, assignment) => acc + assignment.nlc, 0);
    const progress = numCompleted / numLessonsRequiredByEachStudent;
    const classroomStudent = classroomStudents.find((cs) => +cs.user_id === +studentId || +cs.user?.id === +studentId);
    const lastActive = new Date(Math.max(...Object.values(assignments).filter((gp) => gp.dla).map((gp) => new Date(gp.dla).getTime()), 0));
    const totalMsSpent = Object.values(assignments).reduce((acc, assignment) => acc + assignment.t, 0);

    return [ // @TODO: update ids
      {
        id: `${studentId}_display_name`,
        content: (
          <Tooltip title={classroomStudent?.user?.display_name}>
            <span>
              {classroomStudent?.display_name || classroomStudent?.user?.display_name}
            </span>
          </Tooltip>
        ),
        sortValue: classroomStudent?.display_name?.toLocaleLowerCase() || classroomStudent?.user?.display_name?.toLocaleLowerCase(), // @TODO: is this too much duplcation?
      }, // @TODO: maybe surround with join date (or invitation date if pending)
      {
        id: `${studentId}_last_active`,
        content: (lastActive.getTime() > 0
          ? (
            <Tooltip title={`${dateFnsFormat(lastActive, 'MMMM d, yyyy, h:mm a')}`}>
              <span>{dateFnsFormat(lastActive, 'MMM d')}</span>
              {/* @TODO: like this idea, but it should be my own format...`yesterday`, `x days ago`, etc....i don't like the current fallback
              <span>{dateFnsFormatRelative(lastActive, new Date())}</span>
              */}
            </Tooltip>
          )
          : (
            <span>-</span>
          )
        ),
        sortValue: new Date(lastActive || 0).getTime(),
      },
      {
        id: `${studentId}_total_milliseconds_spent`,
        content: !totalMsSpent ? '-' : utilities.humanReadableTimeExact(totalMsSpent), // @TODO: add sortBy attr to use userAssignmentProgress.t instead of the pretty time// @TODO: color code for more than one standard deviation away from mean
        sortValue: totalMsSpent || 0,
      },
      {
        id: `${studentId}_progress`,
        content: (
          <Tooltip title={`${numCompleted} of ${numLessonsRequiredByEachStudent} lessons completed`}>
            <TouchableOpacity onPress={() => setSelectedStudentId(studentId)}>
              <LinearProgress progress={progress} />
            </TouchableOpacity>
          </Tooltip>
        ),
        sortValue: progress,
      },
      /* @TODO:
      {id: `${studentId}_date_started`, content: cs.join_code ? <Tooltip title={dateFnsFormat(cs.created_at, 'MMM d, yyyy, h:mm a')}><span>{dateFnsFormat(cs.created_at, 'MMM d')}</span></Tooltip> : '-'}, // @TODO: replace '-' with tooltip about joining via public code?
      {id: `${studentId}_date_completed`, content: cs.date_joined ? <Tooltip title={dateFnsFormat(cs.date_joined, 'MMM d, yyyy, h:mm a')}><span>{dateFnsFormat(cs.date_joined, 'MMM d')}</span></Tooltip> : '-'},
      */
    ];
  });

  // @TODO: instead of edit buttons opening new dialogs, maybe just toggle editability of each component in-line
  return (
    <WebPageStub
      hideTabs={true}// @TODO: instead of hiding tabs, should we show different tabs for teacher dashboard? i think we should consider it once this single page becomes too complex
    >

      <WebsiteSection
        style={styles.noVerticalPadding}
        isDarkBackground={false}
      >
        <Button// @TODO: shouldn't be full width
          color={styles.colors.primary}
          mode='text'
          icon='arrow-left'
          onPress={() => navigation.navigate(`/schools/${locationState.schoolId}/classrooms/${locationState.classroomId}`)}// @TODO: this is delayed...
        >
          Back to classroom{/* @TODO: i18n */}
        </Button>
      </WebsiteSection>

      {/* @TODO:
      {goal.classroom_goal_metadata.status === 'published' &&
        <WebsiteSection// @TODO: move this to its own component
          isDarkBackground={false}
          title={(
            <span style={styles.currentCampaignsTitleRow}>{/* @TODO: new name /}
              <span>Progress</span>
            </span>
          )}
        >
          <StudentProgressTable classroomId={locationState.classroomId} />
        </WebsiteSection>
      }
      */}

      <WebsiteSection isDarkBackground={false}>
        <View style={styles.headerInfo}>
          <span style={styles.publishedStatusWrapper}>
            <Banner
              type={goal.classroom_goal_metadata.status === 'draft' ? 'warning' : 'success'}
              leftButton={goal.classroom_goal_metadata.status === 'draft' ? <PublishButton classroomId={goal.classroom_goal_metadata.classroom_id} goalId={goal.id} /> : null}// @TODO: this is the right button
              text={goal.classroom_goal_metadata.status === 'draft' ? DRAFT_TEXT : PUBLISHED_TEXT(goal.classroom_goal_metadata.date_published)}
            />
          </span>
          <span style={styles.editRow}>
            <span style={styles.fullTitleWrapper}>
              <h1 style={styles.mainTitle}>{goal.title}</h1>
              <span style={styles.subTitle}>{utilities.prettyAvailablityDateRange(goal.date_available, goal.date_deadline)}</span>
            </span>
            <span style={styles.actionsWrapper}>
              {(isAdmin || goal.classroom_goal_metadata.status === 'draft') &&
                <Tooltip title='Edit goal'>
                  {/* @TODO: issue with back end...teachers shouldn't call the goal patch endpoint right now */}
                  <IconButton
                    icon='pencil'
                    onPress={() => setIsEditGoalDialogOpen(true)}
                  />
                </Tooltip>
              }
            </span>
          </span>
        </View>
        <DraggableFlatList
          keyExtractor={(module: any) => module.id}
          // @TODO: style={styles.list}
          data={_moduleOrderOverrides}
          renderItem={(d) => {
            const module = d.item;

            return (
              <Module
                goal={goal}
                module={module}
                drag={d.drag}
              />
            );
          }}
          showsVerticalScrollIndicator={false}
          activationDistance={1}// @NOTE: needs to be here for web to work
          keyboardDismissMode='on-drag'
          keyboardShouldPersistTaps='handled'
          scrollEnabled={true}
          onDragEnd={({data}) => {
            // @NOTE: temporarily (component state) set note order so that the delay caused by writing to firebase, updating redux, and rerendering doesn't cause a flicker where the dragged note goes back to its original place and then jumps to the new location // @TODO: leftover comment from tibbits
            setModuleOrderOverrides(data);
            dispatch(apiUpdateGoal(goal.id, undefined, undefined, undefined, data.map((m) => m.id), undefined, undefined, undefined, undefined, undefined, undefined));
          }}
        />
        <Accordion
          onChange={() => setIsNewModuleDialogOpen(true)}
          // @TODO: hide chevron (use same prop to have null children and not resize)
          expandable={false}
          title='+ Add new module'
        >
          {null}
        </Accordion>
      </WebsiteSection>

      {!isOakwoodPublicGoal && goal.classroom_goal_metadata.status === 'published' &&
        <WebsiteSection
          isDarkBackground={true}
        >
          <Table
            tableTitle='Student progress'
            open={true}
            lightBackground={true}
            headerRowData={[
              {width: 200, id: 'student', content: 'Student'},
              {width: 100, id: 'last_activity', content: 'Last activity', rightAlign: true},
              {width: 100, id: 'total_time', content: 'Total time spent', rightAlign: true},
              {width: 100, id: 'progress', content: 'Progress', rightAlign: true},
            ]}// @TODO: some of these should be named better and/or have tooltips
            isSortable={true}
            orderByIndex={1}// @TODO: this should be an id, not the header index
            rowData={studentTableRows}
            // @TODO: this should be an accordion to show all assignments for the selected student
            onRowPress={(row) => setSelectedStudentId(+row[0].id.split('_')[0])}// @TODO: way too hacky
          />
        </WebsiteSection>
      }

      {_isEditGoalDialogOpen && <EditGoalDialog isAdmin={isAdmin} goal={goal} onClose={() => setIsEditGoalDialogOpen(false)} />}
      {_isNewModuleDialogOpen && <EditModuleDialog isAdmin={isAdmin} goal={goal} isNew={true} onClose={() => setIsNewModuleDialogOpen(false)} />}
      {_selectedStudentId && <StudentAssignmentsDialog goal={goal} studentId={_selectedStudentId} onClose={() => setSelectedStudentId(null)} />}
    </WebPageStub>
  );
};

export default ClassroomGoal;
